import { Chart } from "react-chartjs-2";
import React, { useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { rgba } from "polished";

import { useTheme } from "@mui/material/styles";
import { Typography, Grid, Box, Button } from "@mui/material";
import { purple, blue, green, red, orange } from "@mui/material/colors";
import { Card as MuiCard, Paper, CardContent as MuiCardContent } from "@mui/material";
import { spacing } from "@mui/system";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import useDashboard from "../../../../hooks/useDashboard";
import useAuth from "../../../../hooks/useAuth";
import useTenant from "../../../../hooks/useTenant";
import sparkleNoise from "../../../../vendor/icons/sparkle-noise";

const twinkle = keyframes`
  0% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.05); }
  100% { opacity: 0.2; transform: scale(1); }
`;

const Card = styled(MuiCard)(spacing);

const AnimatedCard = styled(Card)`
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(props) => props.gradient || "#ffffff"};
  color: ${(props) => (props.gradient ? "#ffffff" : "inherit")};
  border: none;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: ${({ gradient }) =>
    gradient
      ? `0 10px 40px -10px ${rgba(gradient.match(/#[a-fA-F0-9]{3,6}/)?.[0] || "#000", 0.5)}`
      : "0 8px 32px 0 rgba(31, 38, 135, 0.15)"};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.4;
    pointer-events: none;
    background-image: url("${sparkleNoise}"),
      radial-gradient(circle at 20% 30%, #fff 1.5px, transparent 0),
      radial-gradient(circle at 70% 60%, #fff 1.5px, transparent 0),
      radial-gradient(circle at 40% 80%, #fff 1.5px, transparent 0),
      radial-gradient(circle at 80% 20%, #fff 1.5px, transparent 0),
      radial-gradient(circle at 30% 50%, #fff 1.5px, transparent 0);
    background-size:
      200px 200px,
      100px 100px,
      150px 150px,
      120px 120px,
      80px 80px,
      90px 90px;
    mix-blend-mode: screen;
    animation: ${twinkle} 5s ease-in-out infinite;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 50%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ gradient }) =>
      gradient
        ? `0 20px 50px -12px ${rgba(gradient.match(/#[a-fA-F0-9]{3,6}/)?.[0] || "#000", 0.6)}`
        : "0 12px 48px 0 rgba(31, 38, 135, 0.25)"};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardContent = styled(MuiCardContent)`
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(2)};
  }
`;

const ChartWrapper = styled.div`
  height: 168px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const TotalStats = () => {
  const { getDashboardUserCount, dashboadUserCount, isUserDashboardLoading } = useDashboard();
  const { user } = useAuth();
  const { getTenant } = useTenant();
  const navigate = useNavigate();

  const [graphData, setGraphData] = useState(null);
  const hasFetched = useRef(false); // Prevent repeated requests
  const theme = useTheme();

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false, // Disable tooltips
      },
    },
    cutout: "80%",
  };

  const cardData = [
    {
      title: "Total Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #5a3e8e 100%)", // Purple/Indigo gradient
      key: "totalCount",
      link: "/user/userlist/list",
    },
    {
      title: "Active Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 50%, #0083b0 100%)", // Blue gradient
      key: "activeCount",
      link: "/user/active",
    },
    {
      title: "Online Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 50%, #00b09b 100%)", // Green gradient
      key: "onlineCount",
      link: "/user/online",
    },
    {
      title: "Offline Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #8e9eab 0%, #eef2f3 50%, #8e9eab 100%)", // Gray gradient
      key: "offlineCount",
      link: "/user/offline",
    },
    {
      title: "Expired Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #f09819 0%, #edde5d 50%, #f7971e 100%)", // Orange/Yellow gradient
      key: "expireCount",
      link: "/user/expired",
    },
    {
      title: "Suspended Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #ff416c 0%, #ff4b2b 50%, #b21f1f 100%)", // Red gradient
      key: "suspendedCount",
      link: "/user/suspended",
    },
    {
      title: "On Hold Users",
      color: "rgba(255, 255, 255, 0.9)",
      gradient: "linear-gradient(135deg, #8360c3 0%, #2ebf91 50%, #8360c3 100%)", // Purple/Teal gradient
      key: "onHoldCount",
      link: "/user/hold",
    },
  ];

  useEffect(() => {
    if (!user || hasFetched.current) return;

    // Fetch data only once
    const fetchData = async () => {
      await getDashboardUserCount();
      if (user.businessType !== "SUPER_ADMIN") {
        await getTenant(user.tenantId);
      }
      hasFetched.current = true; // Mark as fetched
    };

    fetchData();
  }, [user, getDashboardUserCount, getTenant]);

  if (isUserDashboardLoading || !dashboadUserCount) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          padding: "16px",
          borderRadius: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Button
            startIcon={<ArrowLeft />}
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              textTransform: "none",
              color: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b",
            }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              color: "text.primary",
              margin: 0,
            }}
          >
            Users Overview Statistics
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {cardData.map((card, index) => {
            let value;
            if (card.key === "offlineCount") {
              const active = dashboadUserCount.activeCount || 0;
              const online = dashboadUserCount.onlineCount || 0;
              const offline = Math.max(active - online, 0);
              value = offline === 0 ? "0.01" : offline;
            } else {
              value = dashboadUserCount[card.key] == 0 ? "0.01" : dashboadUserCount[card.key] || 0;
            }
            const data = {
              labels: [card.title],
              datasets: [
                {
                  data: [value],
                  backgroundColor: [card.color],
                  borderWidth: 5,
                  borderColor: theme.palette.background.paper,
                },
              ],
            };

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <AnimatedCard mb={6} gradient={card.gradient}>
                  <CardContent>
                    <Link to={card.link} style={{ textDecoration: "none" }}>
                      <ChartWrapper>
                        <DoughnutInner>
                          <Typography variant="h4" sx={{ color: "#ffffff", fontWeight: 700 }}>
                            {value === "0.01" ? "0" : value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(255, 255, 255, 0.8)", fontWeight: 500 }}
                          >
                            {card.title}
                          </Typography>
                        </DoughnutInner>
                        <Chart
                          type="doughnut"
                          data={{
                            ...data,
                            datasets: [
                              {
                                ...data.datasets[0],
                                borderColor: "transparent",
                                hoverBorderColor: "transparent",
                              },
                            ],
                          }}
                          options={options}
                        />
                      </ChartWrapper>
                    </Link>
                  </CardContent>
                </AnimatedCard>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </>
  );
};

export default TotalStats;
